import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import './SchoolDashboard.css';

function ReportCardManagement() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Common lists
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [gradeLevels, setGradeLevels] = useState([]);
  const [gradeScales, setGradeScales] = useState([]);
  const [gradeScaleEntries, setGradeScaleEntries] = useState({});
  const [selectedGradeLevel, setSelectedGradeLevel] = useState('');
  const [selectedGradeScale, setSelectedGradeScale] = useState('');
  const [selectedTeacherId, setSelectedTeacherId] = useState('');

  // Batch
  const [batches, setBatches] = useState([]);
  const [newBatch, setNewBatch] = useState({ name: '', semester: '', description: '', classId: '' });
  const [selectedBatch, setSelectedBatch] = useState('');

  // Capture
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [subject, setSubject] = useState('');
  const [grades, setGrades] = useState({}); // studentId -> score
  const [saving, setSaving] = useState(false);

  // View
  const [selectedStudent, setSelectedStudent] = useState('');
  const [card, setCard] = useState(null);
  const [cardGrades, setCardGrades] = useState([]);

  useEffect(() => {
    const stored = localStorage.getItem('schoolData');
    if (!stored) {
      navigate('/schoollogin');
      return;
    }
    setSchoolData(JSON.parse(stored));
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (!schoolData?.id) return;
    (async () => {
      const [
        { data: cls },
        { data: tchs },
        { data: bchs },
        { data: lvls },
        { data: scs },
        { data: entries }
      ] = await Promise.all([
        supabase.from('classes').select('*').eq('school_id', schoolData.id).eq('is_active', true).order('class_name'),
        supabase.from('teachers').select('id, first_name, last_name').eq('school_id', schoolData.id).eq('is_active', true).order('first_name'),
        supabase.from('report_batches').select('*').eq('school_id', schoolData.id).order('created_at', { ascending: false }),
        supabase.from('grade_levels').select('*').eq('school_id', schoolData.id).order('created_at', { ascending: true }),
        supabase.from('grade_scales').select('*').eq('school_id', schoolData.id).order('created_at', { ascending: true }),
        supabase.from('grade_scale_entries').select('*').eq('school_id', schoolData.id).order('created_at', { ascending: true })
      ]);

      setClasses(cls || []);
      setTeachers(tchs || []);
      setBatches(bchs || []);
      setGradeLevels(lvls || []);
      setGradeScales(scs || []);

      const groupedEntries = (entries || []).reduce((acc, entry) => {
        const list = acc[entry.scale_id] || [];
        return {
          ...acc,
          [entry.scale_id]: [...list, entry]
        };
      }, {});
      Object.keys(groupedEntries).forEach((key) => {
        groupedEntries[key].sort((a, b) => {
          const aMin = a.min_percentage ?? Number.NEGATIVE_INFINITY;
          const bMin = b.min_percentage ?? Number.NEGATIVE_INFINITY;
          return aMin - bMin;
        });
      });
      setGradeScaleEntries(groupedEntries);
    })();
  }, [schoolData?.id]);

  useEffect(() => {
    if (gradeScales.length && !selectedGradeScale) {
      setSelectedGradeScale(gradeScales[0].id.toString());
    }
  }, [gradeScales, selectedGradeScale]);

  useEffect(() => {
    if (gradeLevels.length && !selectedGradeLevel) {
      setSelectedGradeLevel(gradeLevels[0].id.toString());
    }
  }, [gradeLevels, selectedGradeLevel]);

  useEffect(() => {
    if (teachers.length && !selectedTeacherId) {
      setSelectedTeacherId(teachers[0].id.toString());
    }
  }, [teachers, selectedTeacherId]);

  useEffect(() => {
    if (!selectedClass) { setSections([]); return; }
    const c = classes.find(c => c.id === parseInt(selectedClass));
    if (c?.sections) setSections(c.sections.filter(Boolean).map((s, i) => ({ id: i + 1, name: s })));
    else setSections([]);
  }, [selectedClass, classes]);

  useEffect(() => {
    if (!selectedClass || !selectedSection) { setStudents([]); return; }
    (async () => {
      const { data } = await supabase
        .from('students')
        .select('id, first_name, last_name')
        .eq('school_id', schoolData.id)
        .eq('class_id', selectedClass)
        .eq('section_id', sections.find(s => s.id === parseInt(selectedSection))?.name || '');
      setStudents(data || []);
      // initialize grade entries
      const initial = {};
      (data || []).forEach(s => { initial[s.id] = ''; });
      setGrades(initial);
    })();
  }, [selectedClass, selectedSection, schoolData?.id, sections]);

  const gradingScale = useMemo(() => ({ A: 80, B: 70, C: 60, D: 50 }), []);
  const calcGrade = (score) => {
    const n = Number(score);
    if (isNaN(n)) return '';
    if (n >= gradingScale.A) return 'A';
    if (n >= gradingScale.B) return 'B';
    if (n >= gradingScale.C) return 'C';
    if (n >= gradingScale.D) return 'D';
    return 'E';
  };

  const selectedGradeScaleId = toNumberOrNull(selectedGradeScale);
  const selectedScaleEntries = useMemo(() => {
    if (!selectedGradeScaleId) return [];
    return gradeScaleEntries[selectedGradeScaleId] || [];
  }, [gradeScaleEntries, selectedGradeScaleId]);

  const determineScaleEntry = (score) => {
    const numeric = Number(score);
    if (Number.isNaN(numeric)) return null;
    for (const entry of selectedScaleEntries) {
      const min = entry.min_percentage;
      const max = entry.max_percentage;
      if (min !== null && max !== null) {
        if (numeric >= Number(min) && numeric <= Number(max)) return entry;
      } else if (min !== null && max === null) {
        if (numeric >= Number(min)) return entry;
      } else if (min === null && max !== null) {
        if (numeric <= Number(max)) return entry;
      }
    }
    return null;
  };

  const getGradeLabel = (score) => {
    const entry = determineScaleEntry(score);
    if (entry) {
      return entry.code || entry.label || entry.description || '';
    }
    return calcGrade(score);
  };

  const formatScaleEntryRange = (entry) => {
    const hasMin = entry.min_percentage !== null && entry.min_percentage !== undefined;
    const hasMax = entry.max_percentage !== null && entry.max_percentage !== undefined;

    if (!hasMin && !hasMax) return 'Any score';
    if (hasMin && hasMax) return `${entry.min_percentage}% - ${entry.max_percentage}%`;
    if (hasMin) return `≥ ${entry.min_percentage}%`;
    return `≤ ${entry.max_percentage}%`;
  };

  function toNumberOrNull(value) {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }

  const handleCreateBatch = async (e) => {
    e.preventDefault();
    const payload = {
      school_id: schoolData.id,
      name: newBatch.name || `Report ${newBatch.semester}`,
      semester: newBatch.semester || 'Term',
      description: newBatch.description || null,
      class_ids: newBatch.classId ? [parseInt(newBatch.classId)] : []
    };
    const { data, error } = await supabase.from('report_batches').insert(payload).select().single();
    if (error) return alert('Failed to create batch: ' + error.message);
    setBatches([data, ...batches]);
    setSelectedBatch(data.id.toString());
    alert('Batch created');
  };

  const saveGrades = async () => {
    if (!selectedBatch || !selectedClass || !selectedSection || !subject) {
      return alert('Select batch, class, section, subject');
    }
    const gradeLevelId = toNumberOrNull(selectedGradeLevel);
    const gradeScaleId = toNumberOrNull(selectedGradeScale);
    const teacherIdNum = toNumberOrNull(selectedTeacherId);
    if (!gradeLevelId) return alert('Select a grade level');
    if (!gradeScaleId) return alert('Select a grading scale');
    if (!teacherIdNum) return alert('Select your teacher profile');
    const classId = parseInt(selectedClass, 10);
    const batchId = parseInt(selectedBatch, 10);
    setSaving(true);
    try {
      const sectionName = sections.find(s => s.id === parseInt(selectedSection))?.name || '';
      for (const st of students) {
        const score = grades[st.id];
        if (score === '' || score === undefined) continue;

        // Ensure report_card exists
        let { data: rc } = await supabase
          .from('report_cards')
          .select('*')
          .eq('school_id', schoolData.id)
          .eq('batch_id', batchId)
          .eq('class_id', classId)
          .eq('section_name', sectionName)
          .eq('student_id', st.id)
          .single();
        if (!rc) {
          const { data: newCard, error: cardError } = await supabase.from('report_cards').insert({
            school_id: schoolData.id,
            batch_id: batchId,
            class_id: classId,
            section_name: sectionName,
            student_id: st.id,
            grade_level_id: gradeLevelId,
            grade_scale_id: gradeScaleId,
            teacher_id: teacherIdNum
          }).select().single();
          if (cardError) throw cardError;
          rc = newCard;
        }
        const cardUpdates = {
          grade_level_id: gradeLevelId,
          grade_scale_id: gradeScaleId,
          teacher_id: teacherIdNum
        };
        const needsCardUpdate = rc && (
          rc.grade_level_id !== gradeLevelId ||
          rc.grade_scale_id !== gradeScaleId ||
          rc.teacher_id !== teacherIdNum
        );
        if (needsCardUpdate) {
          await supabase.from('report_cards').update(cardUpdates).eq('id', rc.id);
          rc = { ...rc, ...cardUpdates };
        }

        // Upsert subject grade
        const { data: existing } = await supabase
          .from('report_subject_grades')
          .select('*')
          .eq('report_card_id', rc.id)
          .eq('subject', subject)
          .maybeSingle();
        const gradeEntry = determineScaleEntry(score);
        const payload = {
          report_card_id: rc.id,
          subject,
          score: Number(score),
          grade: gradeEntry ? (gradeEntry.code || gradeEntry.label || gradeEntry.description || '') : calcGrade(score),
          grade_scale_id: gradeScaleId,
          grade_scale_entry_id: gradeEntry?.id || null
        };
        if (existing) await supabase.from('report_subject_grades').update(payload).eq('id', existing.id);
        else await supabase.from('report_subject_grades').insert(payload);
      }
      alert('Grades saved');
    } catch (err) {
      alert('Error saving grades');
    } finally {
      setSaving(false);
    }
  };

  const loadCard = async () => {
    if (!selectedBatch || !selectedStudent) return;
    const { data: rcs } = await supabase
      .from('report_cards')
      .select('*')
      .eq('school_id', schoolData.id)
      .eq('batch_id', selectedBatch)
      .eq('student_id', selectedStudent)
      .maybeSingle();
    setCard(rcs || null);
    if (rcs) {
      const { data: sg } = await supabase
        .from('report_subject_grades')
        .select('*')
        .eq('report_card_id', rcs.id);
      setCardGrades(sg || []);
    } else setCardGrades([]);
  };

  const publishCard = async (val) => {
    if (!card) return;
    const { error } = await supabase.from('report_cards').update({ is_published: val }).eq('id', card.id);
    if (!error) setCard({ ...card, is_published: val });
  };

  const saveComment = async () => {
    if (!card) return;
    const { error } = await supabase.from('report_cards').update({ teacher_comments: card.teacher_comments || null }).eq('id', card.id);
    if (!error) alert('Saved');
  };

  const calcTotals = () => {
    const total = cardGrades.reduce((s, g) => s + Number(g.score || 0), 0);
    let grade = 'E';
    const avg = cardGrades.length ? total / cardGrades.length : 0;
    if (avg >= 80) grade = 'A'; else if (avg >= 70) grade = 'B'; else if (avg >= 60) grade = 'C'; else if (avg >= 50) grade = 'D';
    return { total, grade };
  };

  const printCard = () => {
    window.print();
  };

  const handleLogout = () => navigate('/schoollogin');

  if (loading) return <div className="loading-container"><div className="loading-spinner"></div><p>Loading...</p></div>;
  if (!schoolData) return null;

  return (
    <div className="school-dashboard">
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      <div className="main-content">
        <div className="content-header">
          <h1>📑 Report Card Management</h1>
          <p>Create batches, capture grades, publish and print report cards</p>
        </div>

        {/* Create New Batch */}
        <div className="classes-content">
          <div className="classes-header">
            <h1>Create New Batch</h1>
          </div>
          <div className="classes-table-container" style={{ padding: '1rem' }}>
            <form onSubmit={handleCreateBatch} className="add-student-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Batch Name</label>
                  <input type="text" value={newBatch.name} onChange={(e)=> setNewBatch(prev=>({...prev, name:e.target.value}))} placeholder="e.g., Mid Term 2025" />
                </div>
                <div className="form-group">
                  <label>Semester</label>
                  <input type="text" value={newBatch.semester} onChange={(e)=> setNewBatch(prev=>({...prev, semester:e.target.value}))} placeholder="Semester / Term" />
                </div>
                <div className="form-group">
                  <label>Class</label>
                  <select value={newBatch.classId} onChange={(e)=> setNewBatch(prev=>({...prev, classId:e.target.value}))}>
                    <option value="">Select Class</option>
                    {classes.map(c=> (<option key={c.id} value={c.id}>{c.class_name}</option>))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group" style={{flex:1}}>
                  <label>Description</label>
                  <input type="text" value={newBatch.description} onChange={(e)=> setNewBatch(prev=>({...prev, description:e.target.value}))} placeholder="Description" />
                </div>
                <div className="form-group">
                  <label>&nbsp;</label>
                  <button type="submit" className="submit-btn">Create Batch</button>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Capture Grades */}
        <div className="classes-content" style={{marginTop:'1rem'}}>
          <div className="classes-header">
            <h1>Capture Grades / Marks</h1>
          </div>
          <div style={{padding:'1rem'}}>
            <div className="form-row">
              <div className="form-group">
                <label>Batch</label>
                <select value={selectedBatch} onChange={(e)=> setSelectedBatch(e.target.value)}>
                  <option value="">Select Batch</option>
                  {batches.map(b=> (<option key={b.id} value={b.id}>{b.name} - {b.semester}</option>))}
                </select>
              </div>
              <div className="form-group">
                <label>Class</label>
                <select value={selectedClass} onChange={(e)=> setSelectedClass(e.target.value)}>
                  <option value="">Select Class</option>
                  {classes.map(c=> (<option key={c.id} value={c.id}>{c.class_name}</option>))}
                </select>
              </div>
              <div className="form-group">
                <label>Section</label>
                <select value={selectedSection} onChange={(e)=> setSelectedSection(e.target.value)} disabled={!selectedClass}>
                  <option value="">Select Section</option>
                  {sections.map(s=> (<option key={s.id} value={s.id}>{s.name}</option>))}
                </select>
              </div>
              <div className="form-group">
                <label>Subject</label>
                <input type="text" value={subject} onChange={(e)=> setSubject(e.target.value)} placeholder="e.g., Mathematics" />
              </div>
            </div>
            <div className="form-row" style={{ alignItems: 'flex-end' }}>
              <div className="form-group">
                <label>Grade Level</label>
                <select value={selectedGradeLevel} onChange={(e)=> setSelectedGradeLevel(e.target.value)}>
                  <option value="">Select Grade Level</option>
                  {gradeLevels.map(level => (
                    <option key={level.id} value={level.id}>{level.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Grading Scale</label>
                <select value={selectedGradeScale} onChange={(e)=> setSelectedGradeScale(e.target.value)}>
                  <option value="">Select Grading Scale</option>
                  {gradeScales.map(scale => (
                    <option key={scale.id} value={scale.id}>{scale.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Teacher</label>
                <select value={selectedTeacherId} onChange={(e)=> setSelectedTeacherId(e.target.value)}>
                  <option value="">Select Teacher</option>
                  {teachers.map(t => (
                    <option key={t.id} value={t.id}>{t.first_name} {t.last_name}</option>
                  ))}
                </select>
              </div>
            </div>

            {selectedGradeScaleId && (
              <div
                style={{
                  marginTop: '1rem',
                  padding: '0.85rem 1rem',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  background: '#f8fafc'
                }}
              >
                <strong style={{ display: 'block', marginBottom: '0.35rem' }}>Scale Breakdown</strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {selectedScaleEntries.length === 0 ? (
                    <span style={{ color: '#6b7280' }}>This scale has no entries yet.</span>
                  ) : (
                    selectedScaleEntries.map(entry => (
                      <span
                        key={entry.id}
                        style={{
                          padding: '0.4rem 0.75rem',
                          borderRadius: '999px',
                          background: '#ffffff',
                          border: '1px solid #d1d5db',
                          fontSize: '0.85rem'
                        }}
                      >
                        <strong>{entry.code || entry.label || 'Entry'}</strong>
                        <span style={{ display: 'block', color: '#6b7280' }}>{formatScaleEntryRange(entry)}</span>
                      </span>
                    ))
                  )}
                </div>
              </div>
            )}

            {students.length > 0 && (
              <div className="classes-table-container">
                <table className="classes-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Score</th>
                      <th>Grade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map(st => (
                      <tr key={st.id}>
                        <td>{st.first_name} {st.last_name}</td>
                        <td>
                          <input type="number" min="0" max="100" value={grades[st.id] ?? ''} onChange={(e)=> setGrades(prev=>({...prev, [st.id]: e.target.value}))} style={{width:'100px'}} />
                        </td>
                        <td>{getGradeLabel(grades[st.id])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="form-actions">
                  <button className="submit-btn" disabled={saving} onClick={saveGrades}>{saving ? 'Saving...' : 'Save Grades'}</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* View / Publish */}
        <div className="classes-content" style={{marginTop:'1rem'}}>
          <div className="classes-header"><h1>View / Publish Report Card</h1></div>
          <div style={{padding:'1rem'}}>
            <div className="form-row">
              <div className="form-group">
                <label>Batch</label>
                <select value={selectedBatch} onChange={(e)=> setSelectedBatch(e.target.value)}>
                  <option value="">Select Batch</option>
                  {batches.map(b=> (<option key={b.id} value={b.id}>{b.name} - {b.semester}</option>))}
                </select>
              </div>
              <div className="form-group">
                <label>Student</label>
                <select value={selectedStudent} onChange={(e)=> setSelectedStudent(e.target.value)}>
                  <option value="">Select Student</option>
                  {students.map(s=> (<option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>))}
                </select>
              </div>
              <div className="form-group">
                <label>&nbsp;</label>
                <button className="submit-btn" onClick={loadCard}>Load</button>
              </div>
            </div>

            {card && (
              <div className="classes-table-container">
                <h3>{`Student #${card.student_id}`} {card.is_published ? <span className="status-badge active">Published</span> : <span className="status-badge inactive">Draft</span>}</h3>
                <table className="classes-table">
                  <thead>
                    <tr><th>Subject</th><th>Score</th><th>Grade</th></tr>
                  </thead>
                  <tbody>
                    {cardGrades.map((g,i)=> (<tr key={i}><td>{g.subject}</td><td>{g.score}</td><td>{g.grade}</td></tr>))}
                  </tbody>
                </table>
                <div style={{marginTop:'0.5rem'}}>
                  <textarea rows={3} placeholder="Teacher comments" value={card.teacher_comments || ''} onChange={(e)=> setCard({ ...card, teacher_comments: e.target.value })} style={{width:'100%'}} />
                  <div className="form-actions">
                    <button className="submit-btn" onClick={saveComment}>Save Comment</button>
                    <button className="submit-btn" onClick={()=> publishCard(!card.is_published)}>{card.is_published ? 'Unpublish' : 'Publish'}</button>
                    <button className="submit-btn" onClick={printCard}>Download / Print</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReportCardManagement;


